import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useFieldArray } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import FormDatePicker from "@/components/FormComponents/FormDatePicker";
import { Form } from "@/components/ui/form";
import FormInput from "@/components/FormInput";
import FormCheckbox from "@/components/FormComponents/FormCheckbox";
import { convertDollarsToCents } from "@/lib/utils";
import FormTextarea from "@/components/FormComponents/FormTextarea";
import FormSelectMultiple from "@/components/FormSelectMultiple";
import { getFormattedSources } from "@/util/getFormattedSources";
import { slugify } from "@/util/slugify";
import LoadingButton from "@/components/LoadingButton";
import { useToast } from "@/components/ui/use-toast";
import { useTanFetch } from "@/hooks/useTanFetch";
import useTanMutate from "@/hooks/useTanMutate";
import { UserSettingsWithSources, ApiResponse, User } from "@/types";
import { useLimitCheck } from "@/hooks/useLimitCheck";
import { TierLimitDialog } from "@/components/TierLimitDialog";
import { useState } from "react";
import { useNavigate } from "react-router";

const FormSchema = z.object({
  purchaseName: z.string().min(2, {
    message: "Must be at least 2 characters.",
  }),
  datePurchased: z.date(),
  priceTotal: z.coerce.number().min(0),
  priceTax: z.coerce.number().min(0),
  priceShipping: z.coerce.number().min(0),
  priceFees: z.coerce.number().min(0),
  source: z.array(z.object({ label: z.string(), value: z.string() })),
  sourceUrl: z.url().or(z.literal("")),
  multipleItems: z.boolean().default(false).optional(),
  purchaseNotes: z.string().max(5000).optional(),
  items: z.array(z.object({ itemName: z.string() })),
});

type TAddPurchaseFormSchemaInput = z.input<typeof FormSchema>;
export type TAddPurchaseFormSchema = z.infer<typeof FormSchema>;

export default function AddPurchaseForm({
  closeModal,
}: {
  closeModal: () => void;
}) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [showLimitDialog, setShowLimitDialog] = useState(false);

  const { mutateAsync: pMutate, isPending: postLoading } = useTanMutate({
    key: "purchases",
    reval: ["purchases", "chart-data"],
  });

  const { data: userSettings, isLoading } = useTanFetch<UserSettingsWithSources>(["settings"], true);
  const { data: userData } = useTanFetch<ApiResponse<User>>(["user"], true);

  const subscriptionTier = userData?.data?.subscriptionStatus === 'ACTIVE' ? 'ACTIVE' : 'FREE';

  const purchaseLimit = useLimitCheck({ actionType: 'purchases', subscriptionTier });
  const itemLimit = useLimitCheck({ actionType: 'items', subscriptionTier });

  const form = useForm<TAddPurchaseFormSchemaInput>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      purchaseName: "",
      datePurchased: new Date(),
      priceTotal: 0,
      priceTax: 0,
      priceShipping: 0,
      priceFees: 0,
      source: [],
      sourceUrl: "",
      multipleItems: false,
      purchaseNotes: "",
      items: [{ itemName: "" }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    name: "items",
    control: form.control,
  });

  const formattedSources = getFormattedSources(userSettings as UserSettingsWithSources);

  const onSubmit = async (values: TAddPurchaseFormSchemaInput) => {
    if (values.source.length === 0) {
      form.setError("source", { message: "Click 'create' in the dropdown" });
      return;
    }

    if (form.getValues("source").length === 0) {
      form.setError("source", { message: "Click 'create' in the dropdown" });
    }

    // Clean up blank item names from fieldArray
    const cleanItems = values.multipleItems
      ? values.items.filter((v) => v.itemName !== "")
      : [{ itemName: "" }];

    // Check limits before submitting
    if (!purchaseLimit.canPerform) {
      setShowLimitDialog(true);
      return;
    }

    // Check if items would exceed limits (1 purchase creates 1+ items)
    const itemCount = cleanItems.length || 1;
    if (itemLimit.remainingCount < itemCount) {
      setShowLimitDialog(true);
      return;
    }

    const formattedValues = {
      ...values,
      items: cleanItems,
      source: {
        sourceName: slugify(values.source[0]?.label),
        sourceLabel: values.source[0]?.label,
      },
      priceTotal: convertDollarsToCents(Number(values.priceTotal)),
      priceTax: convertDollarsToCents(Number(values.priceTax)),
      priceShipping: convertDollarsToCents(Number(values.priceShipping)),
      priceFees: convertDollarsToCents(Number(values.priceFees)),
    };

    try {
      await pMutate(formattedValues);

      toast({
        variant: "success",
        description: `${values.purchaseName} added.`,
      });
      closeModal();
    } catch (error: unknown) {
      // Check if it's a tier limit error
      if (error && typeof error === 'object' && 'message' in error) {
        const errorMessage = error.message as string;
        if (errorMessage.includes('TIER_LIMIT_EXCEEDED')) {
          setShowLimitDialog(true);
          return;
        }
      }

      toast({
        title: "Something went wrong...",
        description: "Your purchase was not added. Please try again.",
        variant: "destructive",
      });
    }
  };

  const isMultipleItems = form.watch("multipleItems");

  const handleUpgrade = () => {
    setShowLimitDialog(false);
    closeModal();
    navigate('/subscribe');
  };

  return (
    <div className="">
      <TierLimitDialog
        isOpen={showLimitDialog}
        onOpenChange={setShowLimitDialog}
        actionType="purchases"
        subscriptionTier={subscriptionTier}
        onUpgrade={handleUpgrade}
      />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="max-w-(--breakpoint-md) space-y-4"
        >
          <FormCheckbox
            label="This purchase contains multiple items"
            id="multipleItems"
            form={form}
          />

          {/* Row one */}
          <div className="flex w-full items-center gap-4">
            <FormInput
              itemStyles="grow"
              label={`${isMultipleItems ? "Purchase" : "Item"} Name`}
              id="purchaseName"
              form={form}
            />

            <FormDatePicker
              label="Date Purchased"
              id="datePurchased"
              form={form}
            />
          </div>

          {/* Price row */}
          <div className="flex justify-between gap-4">
            <FormInput label="Total" id="priceTotal" form={form} />
            <FormInput label="Tax" id="priceTax" form={form} />
            <FormInput label="Shipping" id="priceShipping" form={form} />
            <FormInput label="Fees" id="priceFees" form={form} />
          </div>

          {/* Source row */}
          <div className="flex justify-between gap-4">
            <div className="w-[50%] -mb-1">
              {!isLoading && (
                <FormSelectMultiple
                  id="source"
                  label="Source"
                  maxSelected={1}
                  creatable
                  defaultOptions={formattedSources}
                  placeholder="Select or add a source"
                  form={form}
                  // customBadgeClasses="p-0 m-0 max-w-full text-primary-foreground text-md shadow-none"
                />
              )}
            </div>

            <FormInput
              itemStyles="grow"
              label="Source URL"
              id="sourceUrl"
              form={form}
            />
          </div>

          <FormTextarea id="purchaseNotes" form={form} label="Notes" />

          {isMultipleItems && (
            <div className="max-h-[400px] overflow-y-scroll px-2">
              {fields.map((field, index) => {
                return (
                  <div className="flex items-center" key={field.id}>
                    <FormInput
                      label={`${index + 1}. Item Name`}
                      id={`items.${index}.itemName`}
                      fieldName={`items.${index}.itemName`}
                      form={form}
                      itemStyles="grow"
                      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          append({ itemName: "" });
                        }
                      }}
                      afterElement={
                        <Button
                          variant="ghost"
                          className="text-xs uppercase"
                          style={{ display: index === 0 ? "none" : "inline" }}
                          disabled={index === 0}
                          onClick={(e) => {
                            e.preventDefault();
                            remove(index);
                          }}
                        >
                          Remove
                        </Button>
                      }
                    />
                  </div>
                );
              })}

              <Button
                onClick={(e) => {
                  e.preventDefault();
                  append({ itemName: "" });
                }}
              >
                Add Item
              </Button>
            </div>
          )}

          <div className="w-full text-right">
            <LoadingButton
              label="Submit"
              type="submit"
              loading={postLoading}
            />
          </div>
        </form>
      </Form>
    </div>
  );
}
